type CurrentTimeSubscription = {
  updatePeriod: number;
  listener: () => void;
};

const VISIBILITY_CHANGE_EVENT = 'visibilitychange';

/**
 * Class which emits the current time for subscribers. Uses a single interval in combination with useSyncExternalStore to update all subscribers synchronously to prevent tearing.
 * @internal
 */
class CurrentTimeEmitter {
  /** Holds the current time (as of the last update) */
  private currentTime: number;

  /** Handle for the setInterval used to update the current time */
  private interval?: number;

  private stopped = false;

  private subscribers: CurrentTimeSubscription[] = [];

  /** The update period of the current time emitter */
  private updatePeriod = Infinity;

  constructor(
    /**
     * Stores the time of initial render for SSR
     */
    private ssrTime: number
  ) {
    // Capture the current time on first render after SSR so that `getSnapshot()` returns the correct value immediately after hydration
    this.currentTime = Date.now();
  }

  /**
   * `getSnapshot()` function used by `React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)`
   */
  public readonly getCurrentTime = () => this.currentTime;

  /**
   * `getServerSnapshot()` function used by `React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)`
   */
  public readonly getSsrTime = () => this.ssrTime;

  /**
   * Updates the current time and notifies all subscribers.
   */
  private update = (): void => {
    this.currentTime = Date.now();
    this.subscribers.forEach(({ listener }) => listener());
  };

  /**
   * Returns whether the page is visible (i.e. not hidden/backgrounded)
   */
  private getPageVisibility = () => !(document.hidden ?? false);

  /**
   * Handles `document` `'visibilitychange'` events by:
   * - clearing the interval whilst the document is hidden
   * - restarting the interval and updating immediately when the page is resumed (if necessary)
   */
  private onVisibilityChange = (): void => {
    if (!this.getPageVisibility()) {
      window.clearInterval(this.interval);
      delete this.interval;
    } else if (this.subscribers.length) {
      this.interval = window.setInterval(this.update, this.updatePeriod);
      this.update();
    }
  };

  /**
   * Unsubscribes a `listener` from the current time emitter.
   * @param listener The function to unsubscribe
   */
  private unsubscribe = (listener: () => void): void => {
    this.subscribers = this.subscribers.filter(subscriber => subscriber.listener !== listener);

    if (!this.subscribers.length) {
      window.clearInterval(this.interval);
      delete this.interval;

      this.updatePeriod = Infinity;

      document.removeEventListener(VISIBILITY_CHANGE_EVENT, this.onVisibilityChange);
    } else {
      const newUpdatePeriod = Math.min(...this.subscribers.map(({ updatePeriod }) => updatePeriod));
      if (newUpdatePeriod !== this.updatePeriod) {
        window.clearInterval(this.interval);
        this.updatePeriod = newUpdatePeriod;

        if (this.getPageVisibility() && !this.stopped) {
          this.interval = window.setInterval(this.update, this.updatePeriod);
        }

        if (!this.stopped) {
          // Force an asynchronous update just in case the previous interval was about to fire
          Promise.resolve().then(this.update);
        }
      }
    }
  };

  /**
   * Subscribes a `listener` with `updatePeriod` to the current time emitter. Returns a function to unsubscribe the listener.
   * @param listener The function to call when the current time changes
   * @param updatePeriod The interval at which the current time is updated
   * @returns A function to unsubscribe the listener
   */
  public subscribe = (listener: () => void, updatePeriod: number): (() => void) => {
    if (!this.subscribers.length && !this.stopped) {
      document.addEventListener(VISIBILITY_CHANGE_EVENT, this.onVisibilityChange);
    }

    if (updatePeriod < this.updatePeriod) {
      window.clearInterval(this.interval);
      this.updatePeriod = updatePeriod;

      if (this.getPageVisibility() && !this.stopped) {
        this.interval = window.setInterval(this.update, this.updatePeriod);
      }
    }

    this.subscribers.push({ listener, updatePeriod });

    if (!this.stopped) {
      this.update();
    }

    return () => this.unsubscribe(listener);
  };

  /**
   * Restarts the current time emitter by first stopping the emitter, and restarting (if necessary).
   */
  public readonly restart = (): void => {
    this.stop();
    this.stopped = false;

    if (!this.subscribers.length) return;

    document.addEventListener(VISIBILITY_CHANGE_EVENT, this.onVisibilityChange);
    this.onVisibilityChange();
  };

  /**
   * Stops the current time emitter by clearing the interval and removing the page visibility change listener. Used by the `Provider` component to clean up the emitter.
   */
  public readonly stop = (): void => {
    this.stopped = true;
    window.clearInterval(this.interval);
    delete this.interval;
    document.removeEventListener(VISIBILITY_CHANGE_EVENT, this.onVisibilityChange);
  };
}

export { CurrentTimeEmitter };
